import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Sparkles, RefreshCw, MessageCircle, ClipboardList, ArrowLeft, Check, Plus, Save, Sprout } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const INTRO =
  "If you feel this solution could be better, that usually means some need of yours is still unmet, or there's a problem it doesn't yet solve. Tell me what feels off, or what you'd change, and I'll help you name the need beneath it. Whenever you're ready, press \"Review my changes\" to see the needs we've surfaced and regenerate better solutions.";

const norm = (s) => (s || "").trim().toLowerCase();

export default function SolutionChat({ solution, context, onClose, onApplied, onSaveProgress }) {
  const [messages, setMessages] = useState([{ role: "assistant", content: INTRO }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [savingProgress, setSavingProgress] = useState(false);
  const [proposal, setProposal] = useState(null);
  const scrollRef = useRef(null);

  const hasUserMessage = messages.some((m) => m.role === "user");

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/ai/discuss", {
        issue: context.issue,
        position_a: context.position_a,
        position_b: context.position_b,
        needs_a: context.needs_a,
        needs_b: context.needs_b,
        solution,
        messages: next,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSending(false);
  };

  const review = async () => {
    setSummarizing(true);
    try {
      const { data } = await api.post("/ai/summarize-changes", {
        issue: context.issue,
        position_a: context.position_a,
        position_b: context.position_b,
        needs_a: context.needs_a,
        needs_b: context.needs_b,
        messages,
      });
      setProposal(data);
      setReviewing(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSummarizing(false);
  };

  const confirm = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post("/ai/solutions", {
        issue: context.issue,
        position_a: proposal.position_a,
        position_b: proposal.position_b,
        needs_a: proposal.needs_a,
        needs_b: proposal.needs_b,
      });
      const addedA = (proposal.needs_a || []).filter((n) => !context.needs_a?.some((o) => norm(o) === norm(n)));
      const addedB = (proposal.needs_b || []).filter((n) => !context.needs_b?.some((o) => norm(o) === norm(n)));
      onApplied(
        {
          position_a: proposal.position_a,
          position_b: proposal.position_b,
          needs_a: proposal.needs_a,
          needs_b: proposal.needs_b,
          solutions: data.solutions,
        },
        { changes: proposal.changes || [], addedA, addedB }
      );
      const addedCount = addedA.length + addedB.length;
      toast.success(
        addedCount > 0
          ? `Added ${addedCount} new need${addedCount > 1 ? "s" : ""} and regenerated solutions. Previous version saved.`
          : "Solutions regenerated. Previous version saved."
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRegenerating(false);
    }
  };

  const saveProgress = async () => {
    if (!onSaveProgress) return;
    setSavingProgress(true);
    try {
      await onSaveProgress();
    } finally {
      setSavingProgress(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4 bg-[#2D2A26]/40 backdrop-blur-sm"
        onClick={onClose}
        data-testid="solution-chat-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-5xl h-full sm:h-[92vh] flex flex-col bg-[#FDFBF7] sm:rounded-2xl border border-[#E8E3D9] shadow-xl overflow-hidden"
          data-testid="solution-chat-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-6 py-3.5 border-b border-[#E8E3D9] bg-white">
            <p className="text-xs uppercase tracking-[0.15em] text-[#6E6860]">
              {reviewing ? "Review your changes" : "Discuss & improve this solution"}
            </p>
            <div className="flex items-center gap-1.5">
              <button onClick={saveProgress} disabled={savingProgress} data-testid="chat-save-progress" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-[#4A5D4E] border border-[#4A5D4E]/30 hover:bg-[#F5F2EA] transition-colors disabled:opacity-60">
                <Save className={`h-3.5 w-3.5 ${savingProgress ? "animate-pulse" : ""}`} /> Save progress
              </button>
              <button onClick={onClose} data-testid="solution-chat-close" className="p-2 rounded-full text-[#6E6860] hover:bg-[#F5F2EA] transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Two-pane body: solution on the left, chat/review on the right */}
          <div className="flex-1 min-h-0 grid md:grid-cols-2">
            {/* Solution being discussed — always visible */}
            <div className="hidden md:flex flex-col border-r border-[#E8E3D9] bg-white overflow-y-auto" data-testid="chat-solution-context">
              <div className="p-6">
                <p className="text-xs uppercase tracking-[0.15em] text-[#6E6860] mb-1">Solution under discussion</p>
                <h3 className="font-serif text-xl text-[#2D2A26] mb-3">{solution.title}</h3>
                <p className="text-sm text-[#2D2A26] leading-relaxed mb-5">{solution.description}</p>
                <ContextNeeds label="Needs met — your side" color="#4A5D4E" items={solution.needs_satisfied_a} />
                <ContextNeeds label="Needs met — other side" color="#C4A265" items={solution.needs_satisfied_b} />
                {solution.improvements && (
                  <div className="mt-4">
                    <p className="text-xs uppercase tracking-[0.15em] text-[#4A5D4E] mb-1">Possible improvements</p>
                    <p className="text-sm text-[#2D2A26] leading-relaxed">{solution.improvements}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right pane */}
            <div className="flex flex-col min-h-0">
              {/* mobile-only compact solution reminder */}
              <div className="md:hidden px-6 py-3 border-b border-[#E8E3D9] bg-white">
                <p className="text-xs uppercase tracking-[0.15em] text-[#6E6860]">Discussing</p>
                <p className="font-serif text-base text-[#2D2A26]">{solution.title}</p>
              </div>

              {reviewing && proposal ? (
                <ReviewPanel proposal={proposal} setProposal={setProposal} originalA={context.needs_a} originalB={context.needs_b} />
              ) : (
                <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5 space-y-4" data-testid="solution-chat-messages">
                  {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[90%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                          m.role === "user" ? "bg-[#4A5D4E] text-[#FDFBF7]" : "bg-white border border-[#E8E3D9] text-[#2D2A26]"
                        }`}
                      >
                        {m.content}
                      </div>
                    </div>
                  ))}
                  {sending && (
                    <div className="flex justify-start">
                      <div className="rounded-2xl px-4 py-3 bg-white border border-[#E8E3D9] text-[#6E6860] text-sm inline-flex items-center gap-2">
                        <Sparkles className="h-4 w-4 animate-pulse" /> Thinking…
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Footer / actions */}
              <div className="border-t border-[#E8E3D9] bg-white px-6 py-4 space-y-3">
                {reviewing ? (
                  <div className="flex flex-wrap gap-3">
                    <button onClick={confirm} disabled={regenerating} data-testid="review-confirm-button" className="flex-1 min-w-[13rem] inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#4A5D4E] text-[#FDFBF7] font-medium hover:bg-[#3B4A3E] transition-all disabled:opacity-60">
                      {regenerating ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      {regenerating ? "Applying & regenerating…" : "Confirm & regenerate solutions"}
                    </button>
                    <button onClick={() => setReviewing(false)} disabled={regenerating} data-testid="review-keep-discussing-button" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white border border-[#E8E3D9] text-[#2D2A26] hover:bg-[#F5F2EA] transition-all disabled:opacity-60">
                      <ArrowLeft className="h-4 w-4" /> Keep discussing
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-end gap-2">
                      <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            send();
                          }
                        }}
                        rows={1}
                        placeholder="Type your message…"
                        data-testid="solution-chat-input"
                        className="flex-1 resize-none rounded-xl border border-[#E8E3D9] bg-[#FDFBF7] px-4 py-3 text-sm text-[#2D2A26] placeholder:text-[#A39E94] focus:ring-2 focus:ring-[#4A5D4E] focus:outline-none transition max-h-32"
                      />
                      <button onClick={send} disabled={sending || !input.trim()} data-testid="solution-chat-send" className="p-3 rounded-xl bg-[#4A5D4E] text-[#FDFBF7] hover:bg-[#3B4A3E] transition-colors disabled:opacity-50">
                        <Send className="h-4 w-4" />
                      </button>
                    </div>
                    <button onClick={review} disabled={summarizing || !hasUserMessage} data-testid="solution-chat-review" className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#C4A265] text-[#2D2A26] font-medium hover:bg-[#b8955a] transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                      <ClipboardList className={`h-4 w-4 ${summarizing ? "animate-pulse" : ""}`} />
                      {summarizing ? "Gathering your changes…" : "Review my changes"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function ContextNeeds({ label, color, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-4">
      <p className="text-xs uppercase tracking-[0.15em] mb-1.5" style={{ color }}>{label}</p>
      <ul className="space-y-1">
        {items.map((n, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#2D2A26]">
            <Check className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color }} strokeWidth={1.75} />
            <span>{n}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReviewPanel({ proposal, setProposal, originalA, originalB }) {
  const changes = proposal.changes || [];

  const editNeed = (key, i, val) => setProposal({ ...proposal, [key]: proposal[key].map((n, idx) => (idx === i ? val : n)) });
  const removeNeed = (key, i) => setProposal({ ...proposal, [key]: proposal[key].filter((_, idx) => idx !== i) });
  const addNeed = (key, val) => {
    if (!val.trim()) return;
    setProposal({ ...proposal, [key]: [...proposal[key], val.trim()] });
  };

  return (
    <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5" data-testid="review-panel">
      <div>
        <p className="text-xs uppercase tracking-[0.15em] text-[#4A5D4E] mb-2">What will change</p>
        {changes.length === 0 ? (
          <p className="text-sm text-[#6E6860]">
            No concrete changes were detected. You can edit the needs below, keep discussing, or confirm
            to regenerate using the current needs.
          </p>
        ) : (
          <ul className="space-y-2">
            {changes.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#2D2A26]" data-testid={`review-change-${i}`}>
                <Sprout className="h-4 w-4 mt-0.5 shrink-0 text-[#4A5D4E]" strokeWidth={1.75} />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-xs text-[#A39E94]">Newly surfaced needs are marked “New”. You can fine-tune the wording, remove one, or add another before regenerating.</p>

      <div className="space-y-4">
        <EditableNeeds label="Your needs (updated)" accent="#4A5D4E" side="a" needs={proposal.needs_a || []} original={originalA || []} editNeed={editNeed} removeNeed={removeNeed} addNeed={addNeed} />
        <EditableNeeds label="Opponent's needs (updated)" accent="#C4A265" side="b" needs={proposal.needs_b || []} original={originalB || []} editNeed={editNeed} removeNeed={removeNeed} addNeed={addNeed} />
      </div>
    </div>
  );
}

function EditableNeeds({ label, accent, side, needs, original, editNeed, removeNeed, addNeed }) {
  const [draft, setDraft] = useState("");
  const key = side === "a" ? "needs_a" : "needs_b";
  const isNew = (n) => !original.some((o) => norm(o) === norm(n));
  const add = () => {
    addNeed(key, draft);
    setDraft("");
  };
  return (
    <div className="rounded-xl bg-white border border-[#E8E3D9] p-4">
      <p className="text-xs uppercase tracking-[0.15em] mb-3" style={{ color: accent }}>{label}</p>
      <div className="space-y-2">
        {needs.map((n, i) => (
          <div key={i} className={`flex items-center gap-1.5 rounded-lg border pl-3 pr-1.5 py-1 ${isNew(n) ? "bg-[#4A5D4E]/8 border-[#4A5D4E]/40" : "bg-[#F5F2EA] border-[#E8E3D9]"}`} data-testid={`review-need-${side}-${i}`}>
            <input value={n} onChange={(e) => editNeed(key, i, e.target.value)} className="flex-1 bg-transparent text-sm text-[#2D2A26] focus:outline-none" />
            {isNew(n) && (
              <span data-testid={`review-need-new-${side}-${i}`} className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#4A5D4E] text-[#FDFBF7] shrink-0">New</span>
            )}
            <button onClick={() => removeNeed(key, i)} data-testid={`review-need-remove-${side}-${i}`} className="p-1 rounded-full text-[#A39E94] hover:text-[#B27A70] hover:bg-white transition-colors">
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add a need…"
          data-testid={`review-need-add-input-${side}`}
          className="flex-1 rounded-lg border border-[#E8E3D9] bg-white px-3 py-1 text-sm text-[#2D2A26] placeholder:text-[#A39E94] focus:ring-2 focus:ring-[#4A5D4E] focus:outline-none transition"
        />
        <button onClick={add} data-testid={`review-need-add-button-${side}`} className="p-1.5 rounded-lg bg-[#4A5D4E] text-[#FDFBF7] hover:bg-[#3B4A3E] transition-colors">
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
